import { useCallback } from 'react';
import { useFindEmptySpace } from './viewportUtils/useFindEmptySpace';
import { useFindPositionInCampaign } from './viewportUtils/useFindPositionInCampaign';

/**
 * Hook for viewport-related utilities
 * 
 * Provides functions to:
 * - Calculate viewport bounds and center
 * - Find empty space for new nodes
 * - Find position in specific campaign (multi-campaign mode)
 */
export function useViewportUtils({
  containerRef,
  pan,
  zoom,
  tasks,
  nodePositions,
  nodeDimensions,
  customPositionsRef,
}) {
  // ═══════════════════════════════════════════════════════════════
  // VIEWPORT BASICS
  // ═══════════════════════════════════════════════════════════════

  const getViewportCenter = useCallback(() => {
    if (!containerRef.current) return { x: 40, y: 40 };
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = (rect.width / 2 - pan.x) / zoom;
    const centerY = (rect.height / 2 - pan.y) / zoom;
    return { x: Math.round(centerX), y: Math.round(centerY) };
  }, [pan, zoom, containerRef]);

  const getViewportBounds = useCallback(() => {
    if (!containerRef.current) return { left: 0, top: 0, right: 800, bottom: 600 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      left: -pan.x / zoom,
      top: -pan.y / zoom,
      right: (rect.width - pan.x) / zoom,
      bottom: (rect.height - pan.y) / zoom,
    };
  }, [pan, zoom, containerRef]);

  // ═══════════════════════════════════════════════════════════════
  // FIND EMPTY SPACE UTILITIES
  // ═══════════════════════════════════════════════════════════════

  const {
    findEmptySpaceInViewport,
    findEmptySpaceNearNode,
    findEmptySpaceNearPoint,
  } = useFindEmptySpace({
    tasks,
    nodePositions,
    nodeDimensions,
    customPositionsRef,
    getViewportCenter,
    getViewportBounds,
  });

  // ═══════════════════════════════════════════════════════════════
  // MULTI-CAMPAIGN POSITIONING
  // ═══════════════════════════════════════════════════════════════

  const { findPositionInCampaign } = useFindPositionInCampaign({
    tasks,
    nodePositions,
    nodeDimensions,
    customPositionsRef,
    getViewportBounds,
    findEmptySpaceInViewport,
  });

  return {
    getViewportCenter,
    getViewportBounds,
    findEmptySpaceInViewport,
    findEmptySpaceNearNode,
    findEmptySpaceNearPoint,
    findPositionInCampaign,
  };
}
